import { useMemo, memo } from "react";

import { useSelector } from "react-redux";

import type { LeadsSearchAllFiltersTypes } from "app/pages/sales-intelligence/modules/leads-search-filters";

import { ChatMessageError } from "../../../../components/ChatMessage/ChatMessageError";
import { MarkdownMessage } from "../../../../components/MarkdownMessage/MarkdownMessage";
import type { MarkdownMessageProps } from "../../../../components/MarkdownMessage/types";
import { MessageErrorBoundary } from "../../../../components/MessageErrorBoundary";
import { selectMessageError } from "../../../../store";
import { selectProcessedMessageData } from "../../store";
import type { MdComponentProps } from "../../types/mdComponentProps";
import { mapComponentsWithPreprocessedData } from "../../utils/leadGeneratorMarkdownComponents";

type LeadGeneratorMessageProps = Omit<MarkdownMessageProps, "componentProps"> &
    Required<Pick<MarkdownMessageProps<MdComponentProps<LeadsSearchAllFiltersTypes>>, "componentProps">> & {
        messageId: string;
    };

const LeadGeneratorMessage = ({ content, componentProps, messageId, loader }: LeadGeneratorMessageProps) => {
    const preprocessedData = useSelector((state) => selectProcessedMessageData(state, messageId));
    const messageError = useSelector((state) => selectMessageError(state, messageId));

    const components = useMemo(() => {
        const jsonObjects = preprocessedData?.jsonObjects ?? [];

        return mapComponentsWithPreprocessedData(componentProps, jsonObjects, messageId, loader);
    }, [componentProps, preprocessedData?.jsonObjects, messageId, loader]);

    return (
        <MessageErrorBoundary>
            <MarkdownMessage
                content={content}
                components={components}
                componentProps={componentProps}
            />

            {messageError ? <ChatMessageError text={messageError} /> : null}
        </MessageErrorBoundary>
    );
};

export default memo(LeadGeneratorMessage);
